import { useState } from "preact/hooks";
import { useResource } from "@tenergy/web-core/dashboard";
import { adminReads, adminWrites } from "../api/admin-api.js";
import type { AdminRole, DayPartScheduleVersion, DayPartWindow } from "../api/types.js";
import { MutationForm, fieldError } from "../forms/mutation-form.js";
import { count, fromUtcInput, utc } from "../format.js";
import {
  Address,
  Blank,
  ErrorNote,
  Field,
  inputClass,
  Note,
  Spinner,
  Table,
  Td,
  Th,
} from "../ui.js";
import { VersionedPanel } from "./versioned-panel.js";

/**
 * The day-part schedule (pricing.md §1.2b): the windows of the UTC day and the `f_tod` each
 * multiplies the 5 m and 1 h base price by. One version is one whole day, append-only like every
 * other price table here, so "edit" means publishing the next version.
 *
 * The screen checks nothing the API does not: coverage, gaps, overlaps, the [1.00, 3.00] factor
 * range and unique ids are `@tenergy/core`'s rules, and a refusal comes back as `2001` on the
 * `windows` field. The only thing computed here is how much of the day the draft covers, so the
 * operator sees a gap before pressing the button rather than after.
 */

const MINUTES_PER_DAY = 1_440;

export function DayPartsPanel({ role }: { role: AdminRole }) {
  const schedule = useResource((signal) => {
    void signal;
    return adminReads.dayParts({ limit: 50 });
  }, []);
  const active = schedule.data?.active ?? null;

  return (
    <VersionedPanel
      title="Day parts"
      subtitle="The UTC windows of the day and the factor each applies to the 5 m and 1 h base price. The floor still clamps after the factor, on CatFee's own clock."
      role={role}
      formLabel="Publish a day-part schedule"
      renderForm={(done, cancel) => (
        <DayPartsForm
          active={active}
          onDone={() => {
            schedule.reload();
            done();
          }}
          onCancel={cancel}
        />
      )}
    >
      {schedule.loading ? (
        <Spinner />
      ) : schedule.error !== null ? (
        <ErrorNote error={schedule.error} onRetry={() => schedule.reload()} />
      ) : (
        <>
          <div class="mb-2 text-[11px] text-ink-muted">
            In force:{" "}
            <span class="text-ink">
              {active === null
                ? "none — the API refuses to price until a schedule exists"
                : `${active.brand_id ?? "global"}, since ${utc(active.effective_from)}`}
            </span>
          </div>
          <WindowsTable windows={active?.windows ?? []} />
          <h3 class="mt-4 mb-1 num text-[10.5px] font-medium uppercase tracking-[0.08em] text-ink-muted">
            History
          </h3>
          <HistoryTable rows={schedule.data?.versions ?? []} />
        </>
      )}
    </VersionedPanel>
  );
}

function WindowsTable({ windows }: { windows: DayPartWindow[] }) {
  return (
    <Table
      rows={windows.length}
      empty="No schedule is in force."
      head={
        <tr>
          <Th>Window</Th>
          <Th>Label</Th>
          <Th>UTC</Th>
          <Th numeric>Factor</Th>
          <Th numeric>1 h at base 20 SUN</Th>
        </tr>
      }
    >
      {windows.map((w) => (
        <tr key={w.id}>
          <Td mono>{w.id}</Td>
          <Td>{w.label}</Td>
          <Td>
            {clock(w.start_utc_minute)} – {clock(w.end_utc_minute)}
          </Td>
          <Td numeric>
            <span title={`${w.factor_bps} bp`}>×{(w.factor_bps / 10_000).toFixed(2)}</span>
          </Td>
          <Td numeric>
            <span title="Before the floor and the brand markup">{count((20 * w.factor_bps) / 10_000)}</span>
          </Td>
        </tr>
      ))}
    </Table>
  );
}

function HistoryTable({ rows }: { rows: DayPartScheduleVersion[] }) {
  return (
    <Table
      rows={rows.length}
      empty="No schedule has ever been published."
      head={
        <tr>
          <Th>Effective from</Th>
          <Th>Scope</Th>
          <Th>Windows</Th>
          <Th>Changed by</Th>
          <Th>Reason</Th>
        </tr>
      }
    >
      {rows.map((row) => (
        <tr key={row.id}>
          <Td mono>{utc(row.effective_from)}</Td>
          <Td mono>{row.brand_id ?? <span class="text-ink-muted">global</span>}</Td>
          <Td>{summary(row.windows)}</Td>
          <Td>{row.changed_by === null ? <Blank /> : <Address value={row.changed_by} />}</Td>
          <Td>{row.reason ?? <Blank />}</Td>
        </tr>
      ))}
    </Table>
  );
}

function DayPartsForm({
  active,
  onDone,
  onCancel,
}: {
  active: DayPartScheduleVersion | null;
  onDone(): void;
  onCancel(): void;
}) {
  const [text, setText] = useState(active === null ? "" : formatWindows(active.windows));
  const [brandId, setBrandId] = useState("");
  const [effectiveFrom, setEffectiveFrom] = useState("");
  const [reason, setReason] = useState("");
  const [error, setError] = useState<unknown>(null);
  const [busy, setBusy] = useState(false);

  const parsed = parseWindows(text);
  const covered = parsed === null ? null : parsed.reduce((sum, w) => sum + lengthOf(w), 0);
  const draftError =
    parsed === null
      ? "Each line: id HH:MM HH:MM factor_bps label — e.g. peak 14:00 16:00 17000 Peak"
      : covered !== MINUTES_PER_DAY
        ? `The windows cover ${count(covered)} of 1,440 minutes; the API will refuse a gap or an overlap.`
        : null;

  return (
    <MutationForm
      title="Publish a day-part schedule"
      description="The whole day in one version. It replaces the schedule in force from its effective time; quotes already issued keep their price."
      changes={[
        {
          label: "windows",
          before: active === null ? null : summary(active.windows),
          after: parsed === null ? "invalid" : summary(parsed),
        },
        { label: "scope", before: null, after: brandId === "" ? "global" : brandId },
      ]}
      effect={
        effectiveFrom === ""
          ? "60 seconds from now — the same minimum lead time as the retail grid."
          : `${effectiveFrom.replace("T", " ")} UTC. The API refuses anything less than 60 s out.`
      }
      submitLabel="Publish schedule"
      busy={busy}
      disabled={parsed === null || parsed.length === 0}
      error={error}
      reason={reason}
      onReason={setReason}
      onCancel={onCancel}
      onSubmit={() => {
        if (parsed === null) return;
        setBusy(true);
        setError(null);
        void adminWrites
          .publishDayParts({
            windows: parsed,
            ...(brandId === "" ? {} : { brand_id: brandId }),
            ...(effectiveFrom === "" ? {} : { effective_from: fromUtcInput(effectiveFrom) ?? "" }),
            reason,
          })
          .then(onDone)
          .catch(setError)
          .finally(() => setBusy(false));
      }}
    >
      <Field
        label="Windows"
        hint="One per line: id, start and end in UTC, factor in bp (10,000 = ×1.00, up to 30,000), then the label. An end before the start runs across midnight."
        error={fieldError(error, "windows") ?? draftError}
      >
        <textarea
          class={`${inputClass} min-h-40 py-1 num`}
          name="windows"
          value={text}
          onInput={(event) => setText((event.currentTarget as HTMLTextAreaElement).value)}
        />
      </Field>
      <Field
        label="Brand (optional)"
        hint="Empty means the global schedule every brand without one of its own uses."
        error={fieldError(error, "brand_id")}
      >
        <input
          class={inputClass}
          name="brand_id"
          value={brandId}
          onInput={(event) => setBrandId((event.currentTarget as HTMLInputElement).value.trim())}
        />
      </Field>
      <Field
        label="Effective from (UTC)"
        hint="Left empty: 60 s from now. This control has no time zone — what you type is read as UTC."
        error={fieldError(error, "effective_from")}
      >
        <input
          class={inputClass}
          name="effective_from"
          type="datetime-local"
          value={effectiveFrom}
          onInput={(event) => setEffectiveFrom((event.currentTarget as HTMLInputElement).value)}
        />
      </Field>
    </MutationForm>
  );
}

/** `id HH:MM HH:MM factor_bps label…` per line. Null when a line does not read. */
export function parseWindows(text: string): DayPartWindow[] | null {
  const lines = text
    .split("\n")
    .map((line) => line.trim())
    .filter((line) => line !== "");
  const windows: DayPartWindow[] = [];
  for (const line of lines) {
    const match = /^(\S+)\s+(\d{1,2}:\d{2})\s+(\d{1,2}:\d{2})\s+(\d+)\s+(.+)$/.exec(line);
    if (match === null) return null;
    const start = minutes(match[2]!);
    const end = minutes(match[3]!);
    if (start === null || end === null) return null;
    windows.push({
      id: match[1]!,
      label: match[5]!.trim(),
      start_utc_minute: start,
      // "00:00" as an end is the end of the day.
      end_utc_minute: end === 0 ? MINUTES_PER_DAY : end,
      factor_bps: Number(match[4]),
    });
  }
  return windows;
}

export function formatWindows(windows: DayPartWindow[]): string {
  return windows
    .map((w) => `${w.id} ${clock(w.start_utc_minute)} ${clock(w.end_utc_minute)} ${w.factor_bps} ${w.label}`)
    .join("\n");
}

function summary(windows: DayPartWindow[]): string {
  return windows
    .map((w) => `${w.id} ${clock(w.start_utc_minute)}–${clock(w.end_utc_minute)} ×${(w.factor_bps / 10_000).toFixed(2)}`)
    .join(", ");
}

function lengthOf(w: DayPartWindow): number {
  return w.end_utc_minute > w.start_utc_minute
    ? w.end_utc_minute - w.start_utc_minute
    : w.end_utc_minute + MINUTES_PER_DAY - w.start_utc_minute;
}

function minutes(hhmm: string): number | null {
  const [h, m] = hhmm.split(":").map(Number);
  if (h === undefined || m === undefined || h > 24 || m > 59 || (h === 24 && m !== 0)) return null;
  return h * 60 + m;
}

function clock(minute: number): string {
  const wrapped = minute % MINUTES_PER_DAY;
  return `${String(Math.floor(wrapped / 60)).padStart(2, "0")}:${String(wrapped % 60).padStart(2, "0")}`;
}
